import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/solid';
import React, {useState} from 'react';

import {
  Button,
  // Link,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {AfriexRate, AfriexRatePromo, AfriexTier} from '@/types';
import {formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import TierSummary from './TierSummary';
type ExtraRowArg = AfriexTier;

const PromoTiersTable = ({
  items,
  rawAppRates,
  businessId,
  businessName,
  onFeeSaved,
  dataCy,
}: {
  items: AfriexRatePromo[];
  rawAppRates: AfriexRate[];
  extraRow?: (user?: ExtraRowArg) => React.ReactNode;
  businessId?: string;
  businessName?: string;
  /** Called after a promo is updated so the list can refresh in place. */
  onFeeSaved?: () => void | Promise<void>;
  /** Test hook prefix; the table and its cells namespace under it. */
  dataCy?: string;
}) => {
  return (
    <Table
      containerDataCy={dataCy ? `${dataCy}-table` : undefined}
      className="overflow-hidden"
    >
      <Thead>
        <Tr>
          <Th className="w-20">&nbsp;</Th>
          <Th>Name</Th>
          <Th>Discount</Th>
          <Th>Status</Th>
          <Th>Date</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <PromoTiersRow
            key={index}
            item={item}
            rawAppRates={rawAppRates}
            index={index}
            businessId={businessId}
            businessName={businessName}
            onFeeSaved={onFeeSaved}
            dataCy={dataCy}
          />
        ))}
      </TBody>
    </Table>
  );
};

const PromoTiersRow = ({
  item,
  rawAppRates,
  index,
  businessId,
  businessName,
  onFeeSaved,
  dataCy,
}: {
  item: AfriexRatePromo;
  rawAppRates: AfriexRate[];
  index: number;
  businessId?: string;
  businessName?: string;
  onFeeSaved?: () => void | Promise<void>;
  dataCy?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';
  return (
    <>
      <Tr className={zebraRow} data-cy={dataCy ? `${dataCy}-row` : undefined}>
        <Td isNumeric>
          <Button
            dataCy={dataCy ? `${dataCy}-expand` : undefined}
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>

        <Td data-cy={dataCy ? `${dataCy}-name` : undefined}>{item?.name}</Td>
        <Td data-cy={dataCy ? `${dataCy}-discount` : undefined}>
          {formatValues((item?.discount || 0) * 100, 'rate', 6)}%
        </Td>
        <Td data-cy={dataCy ? `${dataCy}-status` : undefined}>
          {!item.meta?.flags?.isDeactivated ? 'ACTIVE' : 'INACTIVE'}
        </Td>
        <Td data-cy={dataCy ? `${dataCy}-date` : undefined}>
          {formatDate.DDMonYYYY(String(item.updatedAt))}
        </Td>
        <Td>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Button>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={6} className="p-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <TierSummary
                item={item}
                rawAppRates={rawAppRates}
                businessId={businessId}
                businessName={businessName}
                onSuccess={onFeeSaved}
              />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default PromoTiersTable;
